import { z } from "zod";
import type { GetArtifactResp } from "./artifacts.ts";
import type { ClaimedArtifactResp, SharedArtifactOwner } from "./sharedArtifact.ts";
import type { ThreadStatus } from "./thread.ts";

/**
 * Shared configuration for the Artifact Library — the single source of truth
 * for which artifacts appear there, what "done" means per type, and how the
 * filter chips group them. Imported by both the backend search type
 * (`modules/search`) and the frontend ArtifactLibrary view.
 *
 * Adding a new artifact type to the Artifact Library is a one-line change here:
 * list it in `ARTIFACT_LIBRARY_TYPES`, give it a terminal status (or `null` for
 * statusless records), and slot it into a chip. It then indexes, lists,
 * filters, searches, and renders without further wiring.
 *
 * Lives in `types/` so it can be shared with `web/` — it must not import from
 * outside `types/`, hence the curated type list is declared literally here
 * rather than derived from the ent schemas.
 */

/**
 * The artifact types surfaced in the Artifact Library — agent-produced outputs
 * plus the two statusless "done" records (callResults, file). This is an
 * allowlist: inbound/scaffolding artifact types (communicationReceived,
 * oauthConnection, routineArtifact, …) are intentionally excluded.
 */
export const ARTIFACT_LIBRARY_TYPES = [
  "document",
  "applet",
  "emailDraft",
  "invitation",
  "textMessageDraft",
  "directMessageDraft",
  "phoneCallDraft",
  "schedulingKickoff",
  "callResults",
  "file",
] as const;

export type ArtifactLibraryType = (typeof ARTIFACT_LIBRARY_TYPES)[number];

/**
 * The Library's one non-artifact result kind: an in-app thread.
 *
 * Deliberately not a member of `ARTIFACT_LIBRARY_TYPES` — that list is the ent
 * allowlist the backend feeds to the artifact search, and threads are not ents.
 * They live in the `threads` table and reach the text index through
 * `tableWrapper` ents that mirror thread titles and message bodies.
 */
export const LIBRARY_THREAD_TYPE = "thread";

/**
 * Everything the `type` query param accepts: every artifact type plus the
 * thread pseudo-type.
 */
export const LIBRARY_SEARCH_TYPES = [
  ...ARTIFACT_LIBRARY_TYPES,
  LIBRARY_THREAD_TYPE,
] as const satisfies readonly string[];

export type LibrarySearchType = (typeof LIBRARY_SEARCH_TYPES)[number];

/**
 * The terminal ("done") `data->>'status'` value(s) that qualify an artifact for
 * the Artifact Library. A type mapped to `null` carries no lifecycle status —
 * it is an immutable completed record (callResults) or a stored asset (file) —
 * and is always included regardless of status.
 */
export const ARTIFACT_LIBRARY_TERMINAL_STATUS: Record<
  ArtifactLibraryType,
  readonly string[] | null
> = {
  document: ["approved"],
  applet: ["approved"],
  emailDraft: ["sent"],
  invitation: ["sent"],
  textMessageDraft: ["sent"],
  directMessageDraft: ["sent"],
  phoneCallDraft: ["completed"],
  schedulingKickoff: ["sent"],
  callResults: null,
  file: null,
};

/**
 * Filter chips for the Artifact Library UI, in display order. `All` spans every
 * Library type; the rest group one or more search types under a friendly label.
 * The `types` of every non-`all` chip must be a subset of
 * `LIBRARY_SEARCH_TYPES`.
 *
 * `Threads` sits directly after `All` rather than at the end of the artifact
 * chips: the Library is reachable from a search dialog on the threads list, and
 * a thread is the likeliest thing someone opening it from there is after.
 */
export const ARTIFACT_LIBRARY_CHIPS = [
  { id: "all", label: "All", types: LIBRARY_SEARCH_TYPES },
  { id: "threads", label: "Threads", types: [LIBRARY_THREAD_TYPE] },
  { id: "documents", label: "Documents", types: ["document"] },
  { id: "applets", label: "Applets", types: ["applet"] },
  { id: "emails", label: "Emails", types: ["emailDraft"] },
  { id: "invites", label: "Invites", types: ["invitation"] },
  { id: "messages", label: "Messages", types: ["textMessageDraft", "directMessageDraft"] },
  { id: "calls", label: "Calls", types: ["phoneCallDraft", "callResults"] },
  { id: "scheduling", label: "Scheduling", types: ["schedulingKickoff"] },
  { id: "files", label: "Files", types: ["file"] },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  types: readonly LibrarySearchType[];
}>;

export type ArtifactLibraryChipId = (typeof ARTIFACT_LIBRARY_CHIPS)[number]["id"];

/**
 * The Sharing filter's options, in display order. Its own axis rather than more
 * `ARTIFACT_LIBRARY_CHIPS` entries because it narrows an orthogonal thing: a
 * chip says what kind of item, this says whose. The two intersect.
 *
 * `all` is the resting value — the Library's whole catalog, unnarrowed — not a
 * third kind of sharing.
 */
export const ARTIFACT_LIBRARY_SHARING_OPTIONS = [
  { id: "all", label: "All" },
  { id: "with-me", label: "Shared with me" },
] as const;

export type ArtifactLibrarySharingId = (typeof ARTIFACT_LIBRARY_SHARING_OPTIONS)[number]["id"];

/**
 * How a Library page is ordered.
 *
 * `relevance` is the default and the historical behaviour: hybrid full-text +
 * vector ranking when there is a query, most-recent-first when there is not.
 * `date` is always most-recent-first, query or no query.
 *
 * The two are not the same search in a different order. Date ordering is done
 * in SQL so it can page exactly at any depth, and SQL ranks on full text only
 * — so the vector half of retrieval is not part of a `date` search, and a
 * result matched purely semantically appears under `relevance` and not under
 * `date`. That is the price of an exact date order over the whole match set,
 * and it is why `relevance` stays the default.
 */
export const LIBRARY_SORTS = ["relevance", "date"] as const;

export type LibrarySort = (typeof LIBRARY_SORTS)[number];

export const DEFAULT_LIBRARY_SORT: LibrarySort = "relevance";

/** Query params for the `GET /api/a/artifact-library` endpoint. */
export const artifactLibraryQuerySchema = z.object({
  /**
   * Free-text query. Absent/empty → recency-ordered listing.
   *
   * Bounded because every term is OR'd into one tsquery and the whole string
   * is embedded for the vector retriever, on a 300ms debounce — an unbounded
   * `q` is free amplification.
   */
  q: z.string().trim().max(256).optional(),
  /**
   * Comma-separated search types to include (a chip's `types`). Absent → every
   * *artifact* type, and no threads.
   *
   * That asymmetry is deliberate and load-bearing. Frontend and backend deploy
   * independently, and a client old enough to predate thread results sends no
   * `type` for its `All` chip. Gating threads behind an explicit mention means
   * such a client keeps receiving exactly what it can render.
   */
  type: z.string().optional(),
  /**
   * How share grants figure in the results.
   *
   * - `all` — the caller's own Library *plus* what others have shared with
   *   them, fused into one list. What the menu's resting value asks for.
   * - `with-me` — only what others have shared.
   * - absent — the caller's own Library alone.
   *
   * Absent is not a synonym for `all`, and the asymmetry is the same one `type`
   * carries for threads: a client older than this feature sends no `sharing`
   * for its own "All", and must keep receiving only the item kinds it can
   * render. Asking for `all` explicitly is how a current client opts in.
   *
   * An enum rather than a boolean so the owner-side direction ("shared with
   * others") widens this param later instead of adding a second one that would
   * have to be kept mutually exclusive with it.
   */
  sharing: z.enum(["all", "with-me"]).optional(),
  /**
   * Result order. Absent → `relevance`, which is what every client that
   * predates this param sends, and what it already received.
   */
  sort: z.enum(LIBRARY_SORTS).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type ArtifactLibraryQuery = z.infer<typeof artifactLibraryQuerySchema>;

/**
 * An artifact result: the same artifact response the thread view renders (which
 * already carries `messageId`), plus the `threadId` the lightbox needs to open
 * it via `HeavyArtifactItem`.
 *
 * `itemKind` is additive on the wire — a client that predates the union ignores
 * it and reads the same fields it always did. It is *not* named `kind` because
 * `bookingArtifact` already owns that key (`kind: "restaurant"`); booking isn't
 * a Library type today, but adding one there is a one-line change and this
 * would silently overwrite it.
 */
export type ArtifactLibraryArtifactItem = GetArtifactResp & {
  itemKind: "artifact";
  threadId: number | null;
};

/**
 * A thread result. Carries no `view` payload and never opens the lightbox: the
 * client navigates to the thread instead.
 */
export type ArtifactLibraryThreadItem = {
  itemKind: "thread";
  threadId: number;
  title: string;
  /** Display status, already derived — render it, don't re-derive. */
  status: ThreadStatus;
  channel: string;
  /** ISO timestamp; the thread's own clock, matching what the row renders. */
  updatedAt: string;
  /**
   * The matched message's indexed text, truncated. Absent when the thread
   * matched on its title alone, or when there was no text query at all.
   */
  snippet?: string;
};

/**
 * An artifact another account has shared with the caller.
 *
 * Built on `ClaimedArtifactResp` rather than on `SharedArtifactPayload`
 * directly, and the difference is load-bearing: `artifactDisplayTitle` and the
 * other claimed-artifact helpers accept `GetArtifactResp | ClaimedArtifactResp`,
 * and the bare payload is neither — it carries no `isOwner` / `canEdit`. Wearing
 * those two literals makes this structurally a claimed artifact, so every such
 * helper takes it unchanged. It is also simply true: the viewer does not own
 * this and cannot edit it.
 *
 * Carries no `threadId` or `messageId` (the payload redacts both), which is why
 * a row of this kind navigates to the artifact page instead of opening the
 * Library lightbox — that surface is thread-scoped.
 */
export type ArtifactLibrarySharedItem = ClaimedArtifactResp & {
  itemKind: "sharedArtifact";
  /**
   * Required here, though optional on `ClaimedArtifactResp`: this arm only ever
   * comes from a backend that populates it, because a client old enough to miss
   * the field is also too old to ask for `sharing=with-me`.
   */
  sharedBy: SharedArtifactOwner;
};

/** One Artifact Library result. Discriminate on `itemKind`. */
export type ArtifactLibraryItem =
  | ArtifactLibraryArtifactItem
  | ArtifactLibraryThreadItem
  | ArtifactLibrarySharedItem;

export type ArtifactLibraryResp = {
  items: ArtifactLibraryItem[];
  /** True when another page may exist (this page filled the requested limit). */
  hasMore: boolean;
  /** Offset to request for the next page. */
  nextOffset: number;
};
